import { formatPeriod, formatSpreadValue, isGameLive } from "./gameStatus";
import { FIRST_SCORING_WEEK, isOffSeason, isScoringWeek } from "./season";

export const TOP_TEAMS_LIMIT = 25;

// Rows shown before the rest of the list is expanded.
export const TOP_TEAMS_COLLAPSED = 10;

// The teams map carries FCS rows too (they appear as opponents), and
// team_season_stats scores them, but nobody can draft one. A leaderboard of
// fantasy teams is FBS only.
const isDraftable = (team) => team?.classification === "fbs";

/**
 * The most recent week any FBS team has points for, or null before the first
 * scoring week. Not config.currentWeek: the week advances before its games are
 * played, so on a Tuesday the current week is empty and the board would be too.
 */
export function latestScoredWeek(teams) {
  let latest = null;
  Object.values(teams || {}).forEach((team) => {
    if (!isDraftable(team)) return;
    Object.keys(team.currentSeason?.weeklyPoints || {}).forEach((key) => {
      const week = Number(key);
      if (!Number.isInteger(week) || !isScoringWeek(week)) return;
      if (latest === null || week > latest) latest = week;
    });
  });
  return latest;
}

/**
 * The last lineup week the season totals may include: the week before
 * config.currentWeek, and never past the latest scored week.
 *
 * Season totals move only when a week ends. During a week, weekly_points holds
 * provisional scores for games in progress, and a season table that shifted
 * every two minutes on a Saturday would contradict its own "through week N"
 * label. The week advances once every game is final, so currentWeek - 1 is the
 * last finished week. Off-season labels mean no week is running, so everything
 * scored counts. Null when no week has finished.
 */
export function seasonThroughWeek(currentWeek, latestWeek) {
  if (latestWeek === null || latestWeek === undefined) return null;
  if (isOffSeason(currentWeek)) return latestWeek;
  const current = parseInt(String(currentWeek).replace(/\D/g, ""), 10);
  if (!Number.isFinite(current)) return latestWeek;
  const through = Math.min(latestWeek, current - 1);
  return through >= FIRST_SCORING_WEEK ? through : null;
}

/**
 * Top FBS teams by fantasy points, for one week or the season to date.
 *
 * mode "week" needs `week`; a team with no entry for it (a bye, or a game not
 * yet scored) is left out rather than ranked at zero. mode "season" needs
 * `throughWeek` and sums weekly_points up to it, rather than reading
 * game_points, which includes the week in progress.
 *
 * `ownership` maps team slug to a whole percentage; a team missing from it is
 * on no roster, so 0.
 *
 * Ties share a rank (1, 2, 2, 4). Order within a tie is season total, then
 * name, so the list is stable between renders. The list is cut at `limit`
 * even mid-tie: a week where fifteen teams scored the maximum would otherwise
 * not be a top twenty five.
 */
export function rankTopTeams(teams, { mode, week, throughWeek, ownership = {}, limit = TOP_TEAMS_LIMIT } = {}) {
  const rows = [];
  Object.entries(teams || {}).forEach(([slug, team]) => {
    if (!isDraftable(team)) return;
    const weekly = team.currentSeason?.weeklyPoints || {};

    // Weeks with an entry, not W-L: a bye writes nothing and week 0 counts
    // for nothing, the same count TeamDrawer averages over.
    let seasonPoints = 0;
    let gamesPlayed = 0;
    Object.entries(weekly).forEach(([w, pts]) => {
      if (!isScoringWeek(w) || typeof pts !== "number") return;
      if (throughWeek != null && Number(w) > throughWeek) return;
      seasonPoints += pts;
      gamesPlayed += 1;
    });

    let points;
    if (mode === "week") {
      const value = weekly[String(week)];
      if (typeof value !== "number") return;
      points = value;
    } else {
      if (gamesPlayed === 0) return;
      points = seasonPoints;
    }

    rows.push({
      slug,
      name: team.name,
      logo: team.logo,
      points,
      seasonPoints,
      gamesPlayed,
      average: gamesPlayed ? Math.round((seasonPoints / gamesPlayed) * 10) / 10 : 0,
      ownership: ownership[slug] || 0,
    });
  });

  rows.sort((a, b) =>
    b.points - a.points ||
    b.seasonPoints - a.seasonPoints ||
    a.name.localeCompare(b.name)
  );

  const ranked = [];
  rows.slice(0, limit).forEach((row, i) => {
    const prev = ranked[i - 1];
    const rank = prev && prev.points === row.points ? prev.rank : i + 1;
    ranked.push({ ...row, rank });
  });
  return ranked;
}

/**
 * get_team_ownership rows to { slug: whole percent }. Rounded, since with a
 * handful of leagues a decimal would claim a precision the sample lacks.
 */
export function ownershipPercentages(rows) {
  const map = {};
  (rows || []).forEach(({ team_id, leagues_owned, leagues_total }) => {
    if (!team_id || !leagues_total) return;
    // Capped at 99 unless every league has it: 499 of 500 rounds to 100, and
    // the 100% band has to mean every league.
    const percent = Math.round((leagues_owned / leagues_total) * 100);
    map[team_id] = leagues_owned < leagues_total ? Math.min(percent, 99) : 100;
  });
  return map;
}

// Ownership shown as a band rather than an exact figure. With a handful of
// leagues a single roster move swings the exact number by double digits, and
// "17%" claims a precision "under 25%" does not. Checked top down, so each
// team lands in the highest band it reaches.
export const OWNERSHIP_BANDS = [
  { min: 100, label: "100%" },
  { min: 90, label: "90%+" },
  { min: 75, label: "75%+" },
  { min: 50, label: "50%+" },
  { min: 25, label: "25%+" },
  { min: 10, label: "<25%" },
  { min: 0, label: "<10%" },
];

export function ownershipBand(percent) {
  const value = Number(percent) || 0;
  return OWNERSHIP_BANDS.find((band) => value >= band.min).label;
}

/**
 * The second line under a team on the Week tab: result, venue, opponent,
 * score, then the line. "W @ Clemson 42-34 · -7.5".
 *
 * `game` is one buildGamesBySlug entry, which is already turned to this
 * team's side: homeScore is this team's score, awayTeam the opponent and
 * homeSpread this team's spread (negative means favoured). A live game shows
 * no W or L, since the result is not in, and carries the quarter instead.
 * Null for a team with no game row that week.
 */
export function formatGameLine(game, teams) {
  if (!game) return null;
  const opponent = teams?.[game.awayTeam]?.school || game.awayTeam;
  const matchup = `${game.isHome ? "vs" : "@"} ${opponent}`;
  const score = `${game.homeScore}-${game.awayScore}`;
  const spread = formatSpreadValue(game.homeSpread);

  let line;
  if (game.gameComplete) {
    const result = game.homeScore > game.awayScore ? "W" : game.homeScore < game.awayScore ? "L" : "T";
    line = `${result} ${matchup} ${score}`;
  } else if (isGameLive(game)) {
    const period = formatPeriod(game.period);
    line = `${matchup} ${score}${period ? ` ${period}` : ""}`;
  } else {
    line = matchup;
  }
  return spread ? `${line} · ${spread}` : line;
}
